/* eslint-disable no-nested-ternary */
import { RESYNC_MAPPING_SETTING, GET_INTEGRATION_SETTINGS } from "lib/constants";
import React, { useState, useEffect, useCallback } from "react";
import { cloneDeep, omit } from "lodash";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";
import Typography from "@mui/material/Typography";
import Accordion from "@mui/material/Accordion";
import BaseButton from "components/atoms/Buttons/BaseButton";
import InputSetting from "components/SettingFields/atoms/InputSetting";
import DatePickerSetting from "components/SettingFields/atoms/DatePickerSetting";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SyncIcon from "@mui/icons-material/Sync";
import SyncAltIcon from "@mui/icons-material/SyncAlt";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import BooleanSetting from "components/SettingFields/atoms/BooleanSetting";
import SingleSelectDropdown from "components/SettingFields/atoms/SingleSelectDropdown";
import { getAPICall, putAPICall } from "lib/callApi";
import TooltipLight from "utils/TooltipLight";
import { Tooltip } from "@mui/material";
import moment from "moment";
import { enqueueSnackbar } from "notistack";

/**
 * Hubspot field types
 * string.text
 * number.number
 * enumeration.select
 * enumeration.checkbox
 * enumeration.booleancheckbox
 * enumeration.radio
 * bool.booleancheckbox
 * string.textarea
 */

const operatorConditionOptions = [
  {
    value: "EqualTo",
    display: "Equals",
  },
  {
    value: "NotEqualTo",
    display: "Does Not Equal",
  },
  {
    value: "StartsWith",
    display: "Starts With",
    fieldTypes: ["textarea", "string", "reference", "picklist"],
  },
  {
    value: "EndsWith",
    display: "Ends With",
    fieldTypes: ["textarea", "string", "reference", "picklist"],
  },
  {
    value: "Contains",
    display: "Contains",
    fieldTypes: [
      "textarea",
      "string",
      "reference",
      "picklist",
      "string.text",
      "string.textarea",
      "enumeration.select",
    ],
  },
  // commented out until Salesforce pushes support for this
  // {
  //   value: "NotContains",
  //   display: "Not Contains",
  //   fieldTypes: ["enumeration.select", "picklist"],
  // },
  {
    value: "LessThan",
    display: "Less Than",
    fieldTypes: ["currency", "number", "date", "datetime", "number.number", "DOUBLE"],
  },
  {
    value: "LessThanOrEqualTo",
    display: "Less Than Or Equal To",
    fieldTypes: ["currency", "number", "date", "datetime", "number.number", "DOUBLE"],
  },
  {
    value: "GreaterThan",
    display: "Greater Than",
    fieldTypes: ["currency", "number", "date", "datetime", "number.number", "DOUBLE"],
  },
  {
    value: "GreaterThanOrEqualTo",
    display: "Greater Than Or Equal To",
    fieldTypes: ["currency", "number", "date", "datetime", "number.number", "DOUBLE"],
  },
  {
    value: "IsNull",
    display: "Is Null",
  },
];

// Note: this is copied from the backend since we dont have a good way to share code
// any updates made to this must be made through the backend first and pass tests
// applies to both functions
const getNumbersFromString = (string) =>
  string
    .toLowerCase()
    .replace(/or|and|\(|\)/g, " ")
    .split(" ")
    .filter((leftoverChar) => leftoverChar && !Number.isNaN(Number(leftoverChar)));

const getCombinationsFromEvaluationString = (str) => {
  if (typeof str !== "string") throw new Error("Argument is not a string");
  const lowercasedStr = str.toLowerCase();
  const possibleCombinations = [];
  if (!lowercasedStr.length) return possibleCombinations;
  let matchesCount = -1;
  const parensMatches = [];
  // eslint-disable-next-line no-useless-escape
  const strAfterParenReplace = lowercasedStr.replace(/(\([^\)]+\))/g, (match) => {
    matchesCount += 1;
    parensMatches.push(match.substring(1, match.length - 1));
    return `{p${matchesCount}}`;
  });

  parensMatches.forEach((nestedCondition) => {
    const splitOnOr = nestedCondition.split(/or/g);
    const numberSplit = splitOnOr.map((match) => match.match(/\d/g).map((strNum) => +strNum));
    possibleCombinations.push(numberSplit);
  });

  const strippedOrMatches = strAfterParenReplace.replace(/or\s\{p\d\}/g, "");
  const splitOnOr = strippedOrMatches.split(/or/g);

  const numberSplit = splitOnOr.map((match) => {
    const matchesToAdd = [];
    const strippedParensMatchReplace = match.replace(/\{p\d\}/g, (nestedMatch) => {
      const numFromMatch = nestedMatch.match(/\d/)[0];
      matchesToAdd.push(possibleCombinations[numFromMatch]);
      possibleCombinations[numFromMatch] = null;
      return "";
    });

    const replacedNumsMatches = getNumbersFromString(strippedParensMatchReplace).map(
      (strNum) => +strNum
    );

    const flatMatchesToAdd = matchesToAdd
      .map((matchGroupToAdd, i) => {
        if (i === 0 && matchesToAdd.length > 1) {
          const copyOfMatchesToAdd = matchesToAdd.slice(1, matchesToAdd.length);
          return matchGroupToAdd.map((nestedMatchGroup) => {
            const assembledGroupings = [];
            copyOfMatchesToAdd.forEach((laterNestedMatchGroup) =>
              laterNestedMatchGroup.forEach((lastNestedGrouping) =>
                assembledGroupings.push([...nestedMatchGroup, ...lastNestedGrouping])
              )
            );
            return assembledGroupings;
          });
        }

        // eslint-disable-next-line no-else-return
        if (i === 0) {
          return matchGroupToAdd;
        }

        return null;
      })
      .filter((grouping) => grouping)
      .flat();

    if (flatMatchesToAdd.length) {
      return flatMatchesToAdd.map((nestedMatch) => [...nestedMatch, ...replacedNumsMatches]);
    }

    return replacedNumsMatches;
  });
  possibleCombinations.push(numberSplit);

  const finalFlattened = possibleCombinations
    .filter((combo) => combo)
    .map((combo) => {
      if (Array.isArray(combo[0][0])) {
        return combo.flat();
      }
      return combo;
    })
    .flat();

  // we need to do one more reduce in case there are any nested arrays still left, those need to combine with everything else in row
  return finalFlattened.reduce((finalGroupings, grouping) => {
    const convertedToArr = Array.isArray(grouping) ? grouping : [grouping];

    const childArrays = convertedToArr.filter((nestedGroup) => Array.isArray(nestedGroup));

    if (childArrays.length) {
      const singleValuesInGrouping = convertedToArr.filter(
        (nestedGroup) => !Array.isArray(nestedGroup)
      );
      childArrays.forEach((nestedArr) => {
        finalGroupings.push([...nestedArr, ...singleValuesInGrouping]);
      });
    } else {
      finalGroupings.push(convertedToArr);
    }

    return finalGroupings;
  }, []);
};

const getVisualEvaluationFromEvaluationStr = (evaluationStr) => {
  try {
    return `Possible Groupings: ${JSON.stringify(
      getCombinationsFromEvaluationString(evaluationStr),
      null,
      2
    )}`;
  } catch (err) {
    console.log("err with formatt");
    console.log(err);

    return "Invalid condition evaluation format";
  }
};

function MappingSetting({ setting, integrationName, setParentState, options = {} }) {
  if (!setting.leftColumn || !setting.rightColumn) return null;
  const { allowDuplicateMappings = true } = options;
  const [showResyncIcon] = useState(setting?.showResyncIcon);
  const [isResyncing, setIsResyncing] = useState(false);
  const [leftColumnOrigLength] = useState(setting.leftColumn.length);
  const [allowsConditions] = useState(setting.allowsConditions);
  const [allowsAlternatePointAssignments] = useState(setting.allowsAlternatePointAssignments);
  const [rightColumnOrigLength] = useState(setting.rightColumn.length);
  const [externalFieldOptions, setExternalFieldOptions] = useState(setting?.externalFieldOptions);
  const mappings = setting.field_value;
  const [usedLeftColumnValues, setUsedLeftColumnValues] = useState(
    mappings
      .map((mapping) => mapping.internal_identifier)
      .reduce((left, leftColumnSelection) => {
        left[leftColumnSelection] = 1;
        return left;
      }, {})
  );
  const [usedRightColumnValues, setUsedRightColumnValues] = useState(
    mappings
      .map((mapping) => mapping.external_identifier)
      .reduce((right, rightColumnSelection) => {
        right[rightColumnSelection] = 1;
        return right;
      }, {})
  );

  const usedLeftColumnLength = Object.keys(usedLeftColumnValues).length;
  const usedRightColumnLength = Object.keys(usedRightColumnValues).length;
  const [showBlankRow, setShowBlankRow] = useState(
    !(
      usedLeftColumnLength === leftColumnOrigLength ||
      usedRightColumnLength === rightColumnOrigLength
    )
  );
  const [blankRowState, setBlankRowState] = useState([
    {
      en_field_label: "",
      field_value: null,
      options: [],
    },
    {
      en_field_label: "",
      field_value: null,
      options: [],
    },
  ]);
  const [visualConditionEvalutions, setVisualConditionEvalutions] = useState({});

  useEffect(() => {
    setShowBlankRow(
      !(
        usedLeftColumnLength === leftColumnOrigLength ||
        usedRightColumnLength === rightColumnOrigLength
      )
    );
  }, [leftColumnOrigLength, rightColumnOrigLength, usedLeftColumnLength, usedRightColumnLength]);

  useEffect(() => {
    setShowBlankRow(
      !(
        usedLeftColumnLength === leftColumnOrigLength ||
        usedRightColumnLength === rightColumnOrigLength
      )
    );
  }, [leftColumnOrigLength, rightColumnOrigLength, usedLeftColumnLength, usedRightColumnLength]);

  useEffect(() => {
    setBlankRowState(
      showBlankRow
        ? [
            {
              en_field_label: setting.leftColumnLabel,
              field_value: null,
              options:
                (allowDuplicateMappings
                  ? setting.leftColumn
                  : setting.leftColumn.filter((leftColumnItem) => {
                      if (usedLeftColumnValues[leftColumnItem.value]) return false;
                      return true;
                    })) || [],
            },
            {
              en_field_label: setting.rightColumnLabel,
              field_value: null,
              options:
                (allowDuplicateMappings
                  ? setting.rightColumn
                  : setting.rightColumn.filter((rightColumnItem) => {
                      if (usedRightColumnValues[rightColumnItem.value]) return false;
                      return true;
                    })) || [],
            },
          ]
        : [
            {
              en_field_label: "",
              field_value: "",
              options: [],
            },
            {
              en_field_label: "",
              field_value: "",
              options: [],
            },
          ]
    );
  }, [showBlankRow, setting, allowDuplicateMappings, usedLeftColumnValues, usedRightColumnValues]);

  const getExternalFieldOptionsApi = useCallback(
    (objectIdentifier) =>
      getAPICall(
        `${GET_INTEGRATION_SETTINGS}/${integrationName}/externalFieldOptions?objectIdentifier=${objectIdentifier}`,
        true
      ),
    []
  );

  const getExternalFieldOptions = useCallback(
    async (objectIdentifier) => {
      const newExternalFieldOptions = {
        ...externalFieldOptions,
      };
      if (!externalFieldOptions[objectIdentifier]?.length && objectIdentifier) {
        const result = await getExternalFieldOptionsApi(objectIdentifier);
        if (result?.data?.success) {
          newExternalFieldOptions[objectIdentifier] = result.data.options;
        }
      }

      if (
        setting?.externalFieldAssociations &&
        setting.externalFieldAssociations[objectIdentifier]?.length
      ) {
        // eslint-disable-next-line no-restricted-syntax
        for (const associatedObject of setting.externalFieldAssociations[objectIdentifier]) {
          if (!externalFieldOptions[associatedObject] && associatedObject) {
            // eslint-disable-next-line no-await-in-loop
            const result = await getExternalFieldOptionsApi(associatedObject);
            if (result?.data?.success) {
              newExternalFieldOptions[associatedObject] = result.data.options;
            }
          }
        }
      }

      setExternalFieldOptions(newExternalFieldOptions);
    },
    [integrationName, externalFieldOptions]
  );

  const formatExternalFieldOptions = (objectIdentifier) => {
    const objectOptions =
      externalFieldOptions?.[objectIdentifier]?.map?.((fieldAndOptions) => ({
        value: fieldAndOptions?.value || "",
        display: fieldAndOptions?.display || "",
        ...(fieldAndOptions?.fieldType ? { fieldType: fieldAndOptions.fieldType } : {}),
      })) || [];

    if (
      setting?.externalFieldAssociations &&
      setting.externalFieldAssociations[objectIdentifier]?.length
    ) {
      // eslint-disable-next-line no-restricted-syntax
      for (const associatedObject of setting.externalFieldAssociations[objectIdentifier]) {
        const associatedObjectOptions = externalFieldOptions[associatedObject] || [];
        if (associatedObjectOptions.length) {
          // eslint-disable-next-line no-restricted-syntax
          for (const associatedObjectOption of associatedObjectOptions) {
            objectOptions.push({
              value: associatedObjectOption?.value || "",
              display:
                `${
                  setting.rightColumn.find((column) => column.value === associatedObject)
                    ?.display || associatedObject
                }: ${associatedObjectOption?.display}` || "",
              apiIdentifier: setting?.apiIdentifiers?.[associatedObject] || associatedObject,
            });
          }
        }
      }
    }
    return objectOptions;
  };

  const sortedLeftColumn = setting.leftColumn.sort((a, b) => {
    if (a.icon && !b.icon) return -1;
    if (!a.icon && b.icon) return 1;
    return 0;
  });

  return (
    <Box sx={{ display: "flex", alignItems: "center", marginBottom: "2rem", width: "100%" }}>
      <Accordion
        sx={{
          width: "100%",
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls={`accordion-${setting.implementation_method_name}-content`}
          id={`accordion-${setting.implementation_method_name}`}
        >
          <Typography>{setting.en_field_label}</Typography>
        </AccordionSummary>
        <AccordionDetails>
          {showResyncIcon && (
            <TooltipLight
              title={isResyncing ? "Currently Syncing" : "Resync"}
              arrow
              key={`resync-tooltip-${setting.FK_integrations_id}`}
              placement="right"
              variant="outline"
            >
              <Box
                sx={{
                  padding: "8px",
                  display: "flex",
                  justifyContent: "flex-end",
                }}
                disabled={isResyncing}
                onClick={async () => {
                  setIsResyncing(true);
                  const resultResponse = await putAPICall(
                    `${RESYNC_MAPPING_SETTING}/${setting.FK_integrations_id}/${setting.id}`,
                    true,
                    null
                  );

                  if (resultResponse?.data?.success) {
                    enqueueSnackbar(
                      JSON.stringify({
                        icon: "success",
                        title: "Mapping Resynced Successfully",
                        text: null,
                      }),
                      {
                        variant: "custom",
                      }
                    );
                  } else {
                    enqueueSnackbar(
                      JSON.stringify({
                        icon: "error",
                        title: "Error",
                        text: resultResponse?.data?.message || "Something Went Wrong",
                      }),
                      {
                        variant: "custom",
                      }
                    );
                  }

                  setIsResyncing(false);
                }}
              >
                <SyncIcon
                  className={isResyncing ? "spin-reverse-icon" : ""}
                  sx={{
                    ":hover": {
                      cursor: isResyncing ? "disabled" : "pointer",
                      color: isResyncing ? "grey" : "black",
                    },
                  }}
                />
              </Box>
            </TooltipLight>
          )}
          {mappings.map(
            (
              {
                internal_identifier: internalIdentifer,
                external_identifier: externalIdentifier,
                mappingId,
                conditions,
                interval,
              },
              index
            ) => {
              const selectedSettingRight = setting.rightColumn.find(
                (rightColumnItem) => rightColumnItem.value === externalIdentifier
              );

              return (
                <Grid
                  container
                  sx={{
                    width: "100%",
                    padding: "24px 4px",
                    marginBottom: "2rem",
                    position: "relative",
                  }}
                  justifyContent="center"
                  alignItems="center"
                  // eslint-disable-next-line react/no-array-index-key
                  key={`mapping-row-${index}`}
                  id={`mapping-container-${
                    mappingId || `${internalIdentifer}-${externalIdentifier}-${index}`
                  }`}
                >
                  {selectedSettingRight?.showFirstEmailToggle && (
                    <BooleanSetting
                      // eslint-disable-next-line react/no-array-index-key
                      key={`show-first-email-toggle-${index}`}
                      setting={{
                        field_value: conditions?.showFirstEmailToggle || 0,
                        en_field_label: "Count First Outbound Email Only",
                      }}
                      integrationName={integrationName}
                      setParentState={(newVal) => {
                        const copied = cloneDeep(mappings);
                        if (!copied[index].conditions) {
                          copied[index].conditions = {};
                        }
                        copied[index].conditions.showFirstEmailToggle = newVal;
                        setParentState(copied);
                      }}
                      styles={{
                        position: "absolute",
                        top: -20,
                        right: 0,
                      }}
                    />
                  )}
                  <Grid item xs={5}>
                    <SingleSelectDropdown
                      setting={{
                        en_field_label: setting.leftColumnLabel,
                        field_value: setting.leftColumn.find(
                          (leftColumnItem) => leftColumnItem.value === internalIdentifer
                        ),
                        options: allowDuplicateMappings
                          ? sortedLeftColumn
                          : sortedLeftColumn.filter((leftColumnItem) => {
                              if (
                                leftColumnItem.value !== internalIdentifer &&
                                usedLeftColumnValues[leftColumnItem.value]
                              )
                                return false;
                              return true;
                            }),
                        groups: setting.groups || null,
                        filterSelectedOptions: !allowDuplicateMappings,
                      }}
                      setParentState={(newVal) => {
                        const copied = cloneDeep(mappings);
                        const internalValue = newVal?.value || null;
                        copied[index].internal_identifier = internalValue;
                        if (!allowDuplicateMappings) {
                          setUsedLeftColumnValues((used) => {
                            const usedCopied = {
                              ...(internalIdentifer ? omit(used, internalIdentifer) : used),
                            };
                            usedCopied[internalValue] = 1;
                            return usedCopied;
                          });
                        }
                        setParentState(copied);
                      }}
                      styles={{
                        marginBottom: 0,
                      }}
                      id={`kpi-single-select-dropdown-${index}`}
                    />
                  </Grid>
                  <Grid
                    item
                    xs={1}
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                    }}
                  >
                    {selectedSettingRight && selectedSettingRight.pullOnly && <ArrowBackIcon />}
                    {selectedSettingRight && !selectedSettingRight.pullOnly && <SyncAltIcon />}
                  </Grid>
                  <Grid item xs={5}>
                    <SingleSelectDropdown
                      setting={{
                        en_field_label: setting.rightColumnLabel,
                        field_value: selectedSettingRight,
                        options: allowDuplicateMappings
                          ? setting.rightColumn
                          : setting.rightColumn.filter((rightColumnItem) => {
                              if (
                                rightColumnItem.value !== externalIdentifier &&
                                usedRightColumnValues[rightColumnItem.value]
                              )
                                return false;
                              return true;
                            }),
                        filterSelectedOptions: !allowDuplicateMappings,
                      }}
                      setParentState={(newVal) => {
                        const externalValue = newVal?.value || null;
                        if (allowsConditions) {
                          getExternalFieldOptions(externalValue);
                        }
                        const copied = cloneDeep(mappings);
                        const oldExternalValue = copied[index].external_identifier;
                        copied[index].external_identifier = externalValue;

                        // clear mappings if we clear or change the value
                        if (oldExternalValue !== externalValue) {
                          copied[index].conditions = { rows: [] };
                        }

                        if (!allowDuplicateMappings) {
                          setUsedRightColumnValues((used) => {
                            const usedCopied = {
                              ...(externalIdentifier ? omit(used, externalIdentifier) : used),
                            };
                            usedCopied[externalValue] = 1;
                            return usedCopied;
                          });
                        }
                        setParentState(copied);
                      }}
                      styles={{
                        marginBottom: 0,
                      }}
                      id={`external-single-select-dropdown-${index}`}
                    />
                  </Grid>
                  <Grid item xs={1}>
                    <Icon
                      fontSize="small"
                      color="error"
                      sx={{
                        cursor: "pointer",
                        marginLeft: "1rem",
                      }}
                      onClick={() => {
                        const copied = cloneDeep(mappings);
                        copied.splice(index, 1);
                        if (!allowDuplicateMappings) {
                          setUsedLeftColumnValues((used) => {
                            const usedCopied = { ...used };
                            delete usedCopied[internalIdentifer];
                            return usedCopied;
                          });
                          setUsedRightColumnValues((used) => {
                            const usedCopied = { ...used };
                            delete usedCopied[externalIdentifier];
                            return usedCopied;
                          });
                        }
                        setParentState(copied);
                      }}
                    >
                      close
                    </Icon>
                  </Grid>
                  <Grid item xs={3} sx={{ marginTop: "16px" }}>
                    {setting?.intervalOptions && (
                      <SingleSelectDropdown
                        setting={{
                          en_field_label: "Sync Interval",
                          field_value: setting.intervalOptions.find(
                            (intervalOption) => intervalOption.value === interval
                          ),
                          options: setting.intervalOptions,
                        }}
                        setParentState={(newVal) => {
                          const selectedInterval = newVal?.value || null;
                          const copied = cloneDeep(mappings);
                          copied[index].interval = selectedInterval;
                          setParentState(copied);
                        }}
                        styles={{
                          marginBottom: 0,
                        }}
                        id={`mapping-interval-options-${index}`}
                      />
                    )}
                  </Grid>
                  {allowsConditions ? (
                    <>
                      {!!conditions?.rows?.length &&
                        conditions.rows.map((conditionRow, conditionRowIndex) => {
                          const objectFields = formatExternalFieldOptions(
                            selectedSettingRight?.value
                          );
                          const fieldOptions =
                            conditionRow?.operator?.value === "IsNull"
                              ? [
                                  { display: "True", value: true },
                                  { display: "False", value: false },
                                ]
                              : externalFieldOptions?.[selectedSettingRight?.value]?.find?.(
                                  (externalField) =>
                                    externalField?.value === conditionRow?.dropdownField?.value
                                )?.options || [];

                          return (
                            <Grid
                              container
                              spacing={2}
                              // eslint-disable-next-line react/no-array-index-key
                              key={`condition-row-${index}-${conditionRowIndex}`}
                              sx={{
                                marginBottom: "1rem",
                                marginTop: "1rem",
                                position: "relative",
                              }}
                            >
                              <Grid item xs={4}>
                                <SingleSelectDropdown
                                  setting={{
                                    en_field_label: `${integrationName} Field`,
                                    field_value: conditionRow.dropdownField,
                                    options: objectFields || [],
                                  }}
                                  setParentState={(selectedOption) => {
                                    const copied = cloneDeep(mappings);
                                    copied[index].conditions.rows[conditionRowIndex].dropdownField =
                                      selectedOption;

                                    copied[index].conditions.rows[
                                      conditionRowIndex
                                    ].dropdownFieldMatchValue = {
                                      value: "",
                                      display: "",
                                    };

                                    copied[index].conditions.rows[conditionRowIndex].operator = {
                                      display: "",
                                      value: "",
                                    };

                                    setParentState(copied);
                                  }}
                                  styles={{
                                    marginBottom: 0,
                                  }}
                                  id={`condition-field-single-select-dropdown-${conditionRowIndex}`}
                                />
                              </Grid>
                              <Grid item xs={3}>
                                <SingleSelectDropdown
                                  disabled={!conditionRow?.dropdownField?.value}
                                  setting={{
                                    en_field_label: "Operator",
                                    field_value: conditionRow.operator,
                                    options: operatorConditionOptions?.filter((operator) => {
                                      if (
                                        integrationName === "Hubspot" &&
                                        ["date.date", "datetime.date"].includes(
                                          conditionRow?.dropdownField?.fieldType
                                        )
                                      )
                                        return operator.value === "IsNull";

                                      return (
                                        !operator?.fieldTypes?.length ||
                                        operator?.fieldTypes?.includes(
                                          conditionRow?.dropdownField?.fieldType
                                        ) ||
                                        false
                                      );
                                    }),
                                  }}
                                  setParentState={(newVal) => {
                                    const operatorValue = newVal?.value || null;
                                    const copied = cloneDeep(mappings);
                                    const foundOperator = operatorConditionOptions.find(
                                      (option) => option.value === operatorValue
                                    );
                                    copied[index].conditions.rows[conditionRowIndex].operator =
                                      foundOperator;

                                    setParentState(copied);
                                  }}
                                  styles={{
                                    marginBottom: 0,
                                  }}
                                  id={`condition-operator-single-select-dropdown-${conditionRowIndex}`}
                                />
                              </Grid>
                              <Grid item xs={4}>
                                {fieldOptions.length ? (
                                  <SingleSelectDropdown
                                    setting={{
                                      en_field_label: "Match Value",
                                      field_value: conditionRow.dropdownFieldMatchValue,
                                      options: fieldOptions,
                                    }}
                                    allowUserInput
                                    setParentState={(newVal) => {
                                      const copied = cloneDeep(mappings);
                                      copied[index].conditions.rows[
                                        conditionRowIndex
                                      ].dropdownFieldMatchValue = newVal;

                                      setParentState(copied);
                                    }}
                                    styles={{
                                      marginBottom: 0,
                                    }}
                                    id={`condition-match-value-single-select-dropdown-${conditionRowIndex}`}
                                  />
                                ) : conditionRow?.dropdownField?.fieldType === "datetime" ? (
                                  <DatePickerSetting
                                    setting={{
                                      en_field_label: "Match Value",
                                      field_value: conditionRow.dropdownFieldMatchValue.value,
                                    }}
                                    setParentState={(e) => {
                                      const currTime = moment(e[0])
                                        .add(moment().hour(), "hours")
                                        .add(moment().minute(), "minutes");

                                      const copied = cloneDeep(mappings);
                                      copied[index].conditions.rows[
                                        conditionRowIndex
                                      ].dropdownFieldMatchValue.value = currTime.toDate();
                                      copied[index].conditions.rows[
                                        conditionRowIndex
                                      ].dropdownFieldMatchValue.display = currTime.toDate();

                                      setParentState(copied);
                                    }}
                                  />
                                ) : (
                                  <InputSetting
                                    setting={{
                                      en_field_label: "Match Value",
                                      field_value: conditionRow.dropdownFieldMatchValue.value,
                                    }}
                                    setParentState={(newVal) => {
                                      const copied = cloneDeep(mappings);
                                      copied[index].conditions.rows[
                                        conditionRowIndex
                                      ].dropdownFieldMatchValue.value = newVal;
                                      copied[index].conditions.rows[
                                        conditionRowIndex
                                      ].dropdownFieldMatchValue.display = newVal;

                                      setParentState(copied);
                                    }}
                                    styles={{
                                      marginBottom: 0,
                                    }}
                                  />
                                )}
                              </Grid>
                              <Grid
                                item
                                xs={1}
                                sx={{
                                  display: "flex",
                                  justifyContent: "flex-start",
                                  alignItems: "center",
                                }}
                              >
                                {conditionRowIndex + 1}
                              </Grid>
                              <Icon
                                fontSize="small"
                                color="error"
                                sx={{
                                  cursor: "pointer",
                                  position: "absolute",
                                  right: "-20px",
                                  top: "0%",
                                }}
                                onClick={() => {
                                  const copied = cloneDeep(mappings);
                                  copied[index].conditions.rows.splice(conditionRowIndex, 1);
                                  const indexRows = [];

                                  for (
                                    let i = 0;
                                    i < copied[index].conditions.rows.length;
                                    i += 1
                                  ) {
                                    indexRows.push(i + 1);
                                  }

                                  copied[index].conditions.evaluation = indexRows.join(" AND ");
                                  setVisualConditionEvalutions((dict) => ({
                                    ...dict,
                                    [index]: getVisualEvaluationFromEvaluationStr(
                                      copied[index].conditions.evaluation
                                    ),
                                  }));
                                  setParentState(copied);
                                }}
                              >
                                close
                              </Icon>
                              {/* This can be brought in but there is no stable way to put a key on this so the ordering doesn't really work when adding or deleting rows */}
                              {/* {fieldOptions.length > 1 && (
                                <>
                                  <Icon
                                    fontSize="medium"
                                    color="black"
                                    sx={{
                                      cursor: "pointer",
                                      position: "absolute",
                                      right: "0px",
                                      top: "45%",
                                      display: conditionRowIndex === 0 ? "none" : "initial",
                                    }}
                                    onClick={() => {
                                      const copied = cloneDeep(mappings);
                                      const copiedConditionRow = cloneDeep(
                                        copied[index].conditions.rows[conditionRowIndex]
                                      );
                                      copied[index].conditions.rows.splice(conditionRowIndex, 1);

                                      copied[index].conditions.rows.splice(
                                        conditionRowIndex - 1,
                                        0,
                                        copiedConditionRow
                                      );

                                      setParentState(copied);
                                    }}
                                  >
                                    arrow_drop_up
                                  </Icon>
                                  <Icon
                                    fontSize="medium"
                                    color="black"
                                    sx={{
                                      cursor: "pointer",
                                      position: "absolute",
                                      right: "-20px",
                                      top: "45%",
                                      display:
                                        conditionRowIndex === conditions.rows.length - 1
                                          ? "none"
                                          : "initial",
                                    }}
                                    onClick={() => {
                                      console.log("hitting me 2");
                                      const copied = cloneDeep(mappings);
                                      const copiedConditionRow = cloneDeep(
                                        copied[index].conditions.rows[conditionRowIndex]
                                      );
                                      copied[index].conditions.rows.splice(conditionRowIndex, 1);

                                      copied[index].conditions.rows.splice(
                                        conditionRowIndex + 1,
                                        0,
                                        copiedConditionRow
                                      );

                                      setParentState(copied);
                                    }}
                                  >
                                    arrow_drop_down
                                  </Icon>
                                </>
                              )} */}
                            </Grid>
                          );
                        })}
                      <Grid container sx={{ marginBottom: "2rem", marginTop: "1rem" }}>
                        <Grid item xs={4}>
                          <BaseButton
                            onClick={() => {
                              const copied = cloneDeep(mappings);
                              if (!copied[index].conditions) {
                                copied[index].conditions = {
                                  rows: [],
                                  evaluation: "",
                                };
                              }

                              if (!copied[index].conditions.rows) {
                                copied[index].conditions = {
                                  ...copied[index].conditions,
                                  rows: [],
                                  evaluation: "",
                                };
                              }

                              copied[index].conditions.rows.push({
                                dropdownField: {
                                  display: "",
                                  value: "",
                                },
                                operator: {
                                  display: "",
                                  value: "",
                                },
                                dropdownFieldMatchValue: {
                                  display: "",
                                  value: "",
                                },
                              });

                              const indexRows = [];

                              for (let i = 0; i < copied[index].conditions.rows.length; i += 1) {
                                indexRows.push(i + 1);
                              }

                              // Make sure we don't overwrite existing eval string
                              if (copied[index].conditions.evaluation) {
                                copied[index].conditions.evaluation = `${
                                  copied[index].conditions.evaluation
                                } AND ${indexRows[indexRows.length - 1]}`;
                              } else {
                                copied[index].conditions.evaluation = indexRows.join(" AND ");
                              }

                              setVisualConditionEvalutions((dict) => ({
                                ...dict,
                                [index]: getVisualEvaluationFromEvaluationStr(
                                  copied[index].conditions.evaluation
                                ),
                              }));

                              setParentState(copied);
                            }}
                            label="Add Condition +"
                            type="secondary"
                            sx={{
                              padding: 0,
                            }}
                            size="secondary"
                          />
                        </Grid>
                      </Grid>
                      {conditions?.rows?.length ? (
                        <Grid item xs={12} sx={{ display: "flex", flexDirection: "column" }}>
                          <Tooltip
                            // eslint-disable-next-line react/jsx-curly-brace-presence
                            title={`Optional: Specify which conditions need to pass in what order to pass the check. An example of an evaluation string is ‘1 AND 2 OR (3 AND 4)’. This says that both 1 and 2 must be true and then either 3 or 4`}
                          >
                            <Icon
                              fontSize="small"
                              color="info"
                              sx={{
                                cursor: "pointer",
                                alignSelf: "flex-end",
                                marginRight: 1.5,
                                marginBottom: "1rem",
                              }}
                            >
                              help
                            </Icon>
                          </Tooltip>
                          <InputSetting
                            // eslint-disable-next-line react/no-array-index-key
                            key={`conditions-evaluation-${index}`}
                            setting={{
                              en_field_label: "Evaluation conditions",
                              field_value: conditions?.evaluation,
                            }}
                            setParentState={(newVal) => {
                              const copied = cloneDeep(mappings);
                              copied[index].conditions.evaluation = newVal;
                              setVisualConditionEvalutions((dict) => ({
                                ...dict,
                                [index]: getVisualEvaluationFromEvaluationStr(newVal),
                              }));

                              setParentState(copied);
                            }}
                            sx={{
                              padding: "16px",
                            }}
                          />
                          <Typography variant="p">
                            {visualConditionEvalutions[index] || ""}
                          </Typography>
                        </Grid>
                      ) : null}
                    </>
                  ) : null}
                  {allowsAlternatePointAssignments && (
                    <>
                      <BooleanSetting
                        // eslint-disable-next-line react/no-array-index-key
                        key={`use-alt-point-assignment-${index}`}
                        setting={{
                          field_value: conditions?.useAlternatePointAssignment || 0,
                          en_field_label: "Alternate User Point Assignment",
                        }}
                        integrationName={integrationName}
                        setParentState={(newVal) => {
                          const copied = cloneDeep(mappings);
                          if (!copied[index].conditions) copied[index].conditions = {};
                          copied[index].conditions.useAlternatePointAssignment = newVal;
                          if (!newVal && copied[index].conditions.alternatePointAssignmentField) {
                            copied[index].conditions.alternatePointAssignmentField = {
                              display: "",
                              value: "",
                            };
                          }
                          setParentState(copied);
                        }}
                      />
                      {!!conditions?.useAlternatePointAssignment && (
                        <>
                          <BooleanSetting
                            // eslint-disable-next-line react/no-array-index-key
                            key={`attribute-owner-as-well-${index}`}
                            setting={{
                              field_value: conditions?.attributeOwnerAsWell || 0,
                              en_field_label: "Atribute Record Owner as well",
                            }}
                            setParentState={(newVal) => {
                              const copied = cloneDeep(mappings);
                              copied[index].conditions.attributeOwnerAsWell = newVal;
                              setParentState(copied);
                            }}
                          />
                          <SingleSelectDropdown
                            name="alternatePointAssignmentField"
                            setting={{
                              field_value: conditions.alternatePointAssignmentField || {
                                display: "",
                                value: "",
                              },
                              options: (
                                externalFieldOptions?.[selectedSettingRight?.value] || []
                              )?.filter(
                                (option) =>
                                  option.referencedObjectType === "OWNER" ||
                                  option.value === "hs_created_by_user_id"
                              ),
                            }}
                            // eslint-disable-next-line react/no-array-index-key
                            key={`alt-point-assignment-${index}`}
                            setParentState={(newVal) => {
                              const copied = cloneDeep(mappings);
                              copied[index].conditions.alternatePointAssignmentField = newVal;
                              setParentState(copied);
                            }}
                            id={`alternate-user-single-select-dropdown-${index}`}
                          />
                        </>
                      )}
                    </>
                  )}
                </Grid>
              );
            }
          )}
          {showBlankRow && (
            <Grid container>
              <Grid item xs={5}>
                <SingleSelectDropdown
                  setting={blankRowState[0]}
                  key={`blank-row-${mappings.length}-left`}
                  setParentState={(newVal) => {
                    const copied = cloneDeep(mappings);
                    const internalVal = newVal?.value || null;
                    copied.push({
                      internal_identifier: internalVal,
                      external_identifier: "",
                    });
                    if (!allowDuplicateMappings) {
                      setUsedLeftColumnValues((used) => {
                        const usedCopied = { ...used };
                        usedCopied[internalVal] = 1;
                        return usedCopied;
                      });
                    }

                    setParentState(copied);
                  }}
                  id="blank-kpi-single-select-dropdown"
                />
              </Grid>
              <Grid item xs={1} />
              <Grid item xs={5}>
                <SingleSelectDropdown
                  setting={blankRowState[1]}
                  key={`blank-row-${mappings.length}-right`}
                  setParentState={(newVal) => {
                    const externalVal = newVal?.value || null;
                    if (allowsConditions) {
                      getExternalFieldOptions(externalVal);
                    }
                    const copied = cloneDeep(mappings);
                    copied.push({
                      internal_identifier: "",
                      external_identifier: externalVal,
                    });
                    if (!allowDuplicateMappings) {
                      setUsedRightColumnValues((used) => {
                        const usedCopied = { ...used };
                        usedCopied[externalVal] = 1;
                        return usedCopied;
                      });
                    }
                    setParentState(copied);
                  }}
                  id="blank-external-single-select-dropdown"
                />
              </Grid>
              <Grid item xs={1} />
            </Grid>
          )}
        </AccordionDetails>
      </Accordion>
    </Box>
  );
}

export default MappingSetting;
